import { formatFractionalValue, formatMeasureText } from '@ordo/constants/measure-format'

import type { Deliverable, Temperature } from '@/lib/chefs/types'

/** Display label for a deliverable's storage temperature (Hot / Chilled / Ambient). */
export function temperatureLabel(temperature: Temperature): string {
  if (temperature === 'hot') {
    return 'Hot'
  }
  return temperature === 'ambient' ? 'Ambient' : 'Chilled'
}

/**
 * A COUNT of servings, not a measure — "240x" means two hundred and forty of
 * the thing. Deliberately not routed through the measure formatter: there is
 * no unit to convert and no rounding to apply.
 */
export function formatServingCount(qty: number): string {
  return `${qty}x`
}

export function formatQuantity(qty: number, unit: string): string {
  if (unit === 'x') {
    return formatServingCount(qty)
  }
  return formatMeasureText(qty, unit, { system: 'as-authored' })
}

export function formatDeliverableQuantity(d: Deliverable): string {
  return formatQuantity(d.qty, d.unit)
}

/**
 * Render a serving amount, collapsing common fractions (0.75 → "3/4").
 *
 * The implementation lives in `@ordo/constants/measure-format` so the same
 * fraction table serves cook-facing rendering everywhere, including
 * server-rendered documents. Re-exported here for the existing call sites.
 */
export const formatServingAmount = formatFractionalValue

/**
 * Recipe-based serving label ("2 Pancakes", "1/2 Cup") — PROD-173. Null when
 * either part is missing so callers can skip the sublabel entirely.
 */
export function formatRecipeServing(
  quantity: number | null | undefined,
  unit: string | null | undefined,
): string | null {
  if (quantity === null || quantity === undefined || !Number.isFinite(quantity) || quantity <= 0 || !unit) {
    return null
  }
  return `${formatServingAmount(quantity)} ${unit}`
}

export function formatDateRange(start: Date, end: Date): string {
  const sameYear = start.getFullYear() === end.getFullYear()
  const sameMonth = sameYear && start.getMonth() === end.getMonth()
  const startMonth = start.toLocaleDateString('en-US', { month: 'short' })
  const endMonth = end.toLocaleDateString('en-US', { month: 'short' })
  const year = end.getFullYear()
  if (sameMonth) {
    return `${startMonth} ${start.getDate()}–${end.getDate()}, ${year}`
  }
  if (sameYear) {
    return `${startMonth} ${start.getDate()} – ${endMonth} ${end.getDate()}, ${year}`
  }
  return `${startMonth} ${start.getDate()}, ${start.getFullYear()} – ${endMonth} ${end.getDate()}, ${year}`
}

export function formatSingleDate(date: Date): string {
  return date.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })
}

function pad2(n: number): string {
  return String(n).padStart(2, '0')
}

const SHORT_WEEKDAY = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

export function formatShortDay(date: Date): string {
  return `${SHORT_WEEKDAY[date.getDay()]} ${pad2(date.getMonth() + 1)}/${pad2(date.getDate())}`
}

export function formatShortDayRange(start: Date, end: Date): string {
  return `${formatShortDay(start)} – ${formatShortDay(end)}`
}

/**
 * Chef-facing shipment date — always the DELIVERY axis. A kitchen's day is
 * the day the truck leaves, so chef surfaces show one date and it is this
 * one; the service date is intentionally not surfaced here.
 *
 * Shipment dates are UTC-midnight instants (`@db.Date`), so formatting must
 * stay in UTC — a local-time render shows the previous day everywhere west
 * of Greenwich.
 */
export function formatDeliveryDate(shipment: { deliveryDate: string }): string {
  return new Date(shipment.deliveryDate).toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  })
}
